import dayjs from "dayjs";
import { dateFormat, getPointsBadge, getTeamLogo } from "@/lib/utils";
import { Loader } from "../common/loader";
import { type PlayerData, type PlayerKpi, type GoalkeeperKpi } from "@/api/player";

interface PlayerMetricsProps {
    playerData?: PlayerData;
    isLoading?: boolean;
}

const processMetrics = (playerData: PlayerData) => {
    const isGoalkeeper = playerData.role.includes("GK");
    const kpis =
        isGoalkeeper && (playerData.goalkeeperKpis?.length ?? 0) > 0
            ? playerData.goalkeeperKpis!
            : (playerData.kpis ?? []);

    if (kpis.length === 0) return null;

    const highestKpiMatch = kpis.reduce((max, k) => (k.totalPoints > max.totalPoints ? k : max));
    const getOpponent = (k: PlayerKpi | GoalkeeperKpi) => {
        const isHome =
            playerData.teamId !== undefined && (k.homeTeamId !== undefined || k.awayTeamId !== undefined)
                ? playerData.teamId === k.homeTeamId
                : k.homeTeam === " ";
        return isHome ? k.awayTeam : k.homeTeam;
    };

    let wins = 0,
        losses = 0;
    kpis.forEach((k) => {
        const isHome =
            playerData.teamId !== undefined && (k.homeTeamId !== undefined || k.awayTeamId !== undefined)
                ? playerData.teamId === k.homeTeamId
                : k.homeTeam === " ";
        const vg = isHome ? k.homeGoals : k.awayGoals;
        const og = isHome ? k.awayGoals : k.homeGoals;
        if (vg > og) wins++;
        else if (vg < og) losses++;
    });

    const kpiPerRole = playerData.kpiPerRole ?? [];
    const overallEntry = kpiPerRole.find((r) => r.role === "Overall");
    const averageKPI =
        overallEntry?.avgKpi ??
        (kpiPerRole.length > 0 ? kpiPerRole.reduce((s, r) => s + (r.avgKpi ?? 0), 0) / kpiPerRole.length : 0);
    const matchesTotal =
        overallEntry?.matchesPlayed ??
        (kpiPerRole.length > 0 ? Math.max(...kpiPerRole.map((r) => r.matchesPlayed ?? 0)) : null) ??
        kpis.length;

    return {
        averageKPI,
        highestKPI: {
            value: highestKpiMatch.totalPoints,
            date: highestKpiMatch.matchDate,
            opponent: getOpponent(highestKpiMatch),
        },
        matchesPlayed: { total: matchesTotal, wins, losses },
    };
};

const StatBlock = ({
    label,
    value,
    subtitle,
    progress,
    accent = false,
    loading = false,
    showBadge = false,
}: {
    label: string;
    value: string | number;
    subtitle: string;
    progress: number;
    accent?: boolean;
    loading?: boolean;
    showBadge?: boolean;
}) => {
    const numVal = typeof value === "string" ? parseFloat(value) : value;
    const badge = !isNaN(numVal) && numVal > 10 ? getPointsBadge(numVal) : null;

    return (
        <div
            className="bg-surface-container-highest px-4 py-3 rounded-xl flex flex-col justify-between"
            style={accent ? { borderLeft: "4px solid #ba0a06" } : undefined}
        >
            <p className="font-label text-[10px] uppercase tracking-widest text-primary font-bold">{label}</p>
            <div className="mt-2 flex items-baseline gap-2">
                {loading ? (
                    <Loader message="" className="text-sm" />
                ) : (
                    <>
                        {showBadge && badge ? (
                            <div
                                className={`inline-flex items-center px-2 py-0.5 ${badge.bg} ${badge.text} rounded font-headline font-black text-2xl md:text-3xl border ${badge.border}`}
                            >
                                {value}
                            </div>
                        ) : (
                            <span className="font-headline text-2xl md:text-3xl font-black text-on-surface">
                                {value}
                            </span>
                        )}
                        <span className="text-xs font-medium text-on-surface-variant">{subtitle}</span>
                    </>
                )}
            </div>
            <div className="mt-3 h-0.5 w-full bg-outline-variant/20 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                        width: `${Math.min(Math.max(progress * 100, 2), 100)}%`,
                        background: accent ? "#ba0a06" : "#050066",
                    }}
                />
            </div>
        </div>
    );
};

const CompactStatBlock = ({
    label,
    value,
    subtitle,
    subtitleNode,
    progress,
    loading = false,
    showBadge = false,
}: {
    label: string;
    value: string | number;
    subtitle: string;
    subtitleNode?: React.ReactNode;
    progress: number;
    loading?: boolean;
    showBadge?: boolean;
}) => {
    const numVal = typeof value === "string" ? parseFloat(value) : value;
    const badge = !isNaN(numVal) && numVal > 10 ? getPointsBadge(numVal) : null;

    return (
        <div className="bg-surface-container-highest px-3 py-3 rounded-xl flex flex-col justify-between min-h-[88px]">
            <p className="font-label text-[9px] uppercase tracking-widest text-primary font-bold leading-none">
                {label}
            </p>
            <div className="flex flex-col items-start">
                {loading ? (
                    <Loader message="" className="text-xs" />
                ) : showBadge && badge ? (
                    <div
                        className={`inline-flex items-center px-1.5 py-0.5 ${badge.bg} ${badge.text} rounded font-headline font-black text-xl border ${badge.border}`}
                    >
                        {value}
                    </div>
                ) : (
                    <span className="font-headline text-xl font-black text-on-surface leading-none">{value}</span>
                )}
                {!loading &&
                    (subtitleNode !== undefined
                        ? subtitleNode
                        : subtitle && (
                              <span className="text-[9px] text-on-surface-variant mt-0.5 leading-tight truncate">
                                  {subtitle}
                              </span>
                          ))}
            </div>
            <div className="h-0.5 w-full bg-outline-variant/20 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                        width: `${Math.min(Math.max(progress * 100, 2), 100)}%`,
                        background: "#050066",
                    }}
                />
            </div>
        </div>
    );
};

export function PlayerMetrics({ playerData, isLoading }: PlayerMetricsProps) {
    const metrics = playerData ? processMetrics(playerData) : null;

    if (isLoading && !metrics) {
        return (
            <>
                <div className="grid grid-cols-3 gap-2 lg:hidden">
                    <CompactStatBlock label="Avg KPI" value="—" subtitle="" progress={0} loading />
                    <CompactStatBlock label="Best KPI" value="—" subtitle="" progress={0} loading />
                    <CompactStatBlock label="Matches" value="—" subtitle="" progress={0} loading />
                </div>
                <div className="hidden lg:block">
                    <StatBlock label="Avg KPI" value="—" subtitle="" progress={0} loading />
                </div>
                <div className="hidden lg:block">
                    <StatBlock label="Highest KPI" value="—" subtitle="" progress={0} loading />
                </div>
                <div className="hidden lg:block">
                    <StatBlock label="Matches" value="—" subtitle="" progress={0} loading />
                </div>
            </>
        );
    }

    if (!metrics) {
        return (
            <>
                <div className="grid grid-cols-3 gap-2 lg:hidden">
                    {["Avg KPI", "Best KPI", "Matches"].map((label) => (
                        <div
                            key={label}
                            className="bg-surface-container-highest px-3 py-3 rounded-xl flex items-center justify-center min-h-[88px]"
                        >
                            <span className="text-on-surface-variant text-xs">—</span>
                        </div>
                    ))}
                </div>
                {["Avg KPI", "Highest KPI", "Matches"].map((label) => (
                    <div key={label} className="hidden lg:block">
                        <div className="bg-surface-container-highest p-6 rounded-xl flex items-center justify-center">
                            <span className="text-on-surface-variant text-sm">No data</span>
                        </div>
                    </div>
                ))}
            </>
        );
    }

    const avgSubtitle = "tournament avg";
    const highestSubtitle = `vs ${metrics.highestKPI.opponent} · ${dayjs(metrics.highestKPI.date).format(dateFormat)}`;
    const matchesSubtitle = `${metrics.matchesPlayed.wins}W · ${metrics.matchesPlayed.losses}L`;
    const opponentLogo = getTeamLogo(metrics.highestKPI.opponent);
    const bestKpiSubtitleNode = (
        <div className="flex items-center gap-1 mt-0.5">
            {opponentLogo ? (
                <img src={opponentLogo} alt={metrics.highestKPI.opponent} className="w-4 h-4 object-contain shrink-0" />
            ) : (
                <span className="text-[9px] text-on-surface-variant truncate">{metrics.highestKPI.opponent}</span>
            )}
            <span className="text-[9px] text-on-surface-variant shrink-0">
                {dayjs(metrics.highestKPI.date).format(dateFormat)}
            </span>
        </div>
    );

    return (
        <>
            <div className="grid grid-cols-3 gap-2 lg:hidden">
                <CompactStatBlock
                    label="Avg KPI"
                    value={metrics.averageKPI.toFixed(1)}
                    subtitle={avgSubtitle}
                    progress={metrics.averageKPI / 100}
                    showBadge
                />
                <CompactStatBlock
                    label="Best KPI"
                    value={metrics.highestKPI.value.toFixed(1)}
                    subtitle={highestSubtitle}
                    subtitleNode={bestKpiSubtitleNode}
                    progress={metrics.highestKPI.value / 100}
                    showBadge
                />
                <CompactStatBlock
                    label="Matches"
                    value={metrics.matchesPlayed.total}
                    subtitle={matchesSubtitle}
                    progress={
                        metrics.matchesPlayed.total > 0 ? metrics.matchesPlayed.wins / metrics.matchesPlayed.total : 0
                    }
                />
            </div>
            <div className="hidden lg:block">
                <StatBlock
                    label="Avg KPI"
                    value={metrics.averageKPI.toFixed(1)}
                    subtitle={avgSubtitle}
                    progress={metrics.averageKPI / 100}
                    showBadge
                />
            </div>
            <div className="hidden lg:block">
                <StatBlock
                    label="Highest KPI"
                    value={metrics.highestKPI.value.toFixed(1)}
                    subtitle={highestSubtitle}
                    progress={metrics.highestKPI.value / 100}
                    showBadge
                />
            </div>
            <div className="hidden lg:block">
                <StatBlock
                    label="Matches Played"
                    value={metrics.matchesPlayed.total}
                    subtitle={matchesSubtitle}
                    progress={
                        metrics.matchesPlayed.total > 0 ? metrics.matchesPlayed.wins / metrics.matchesPlayed.total : 0
                    }
                />
            </div>
        </>
    );
}
